/**
 * Deterministic SOUL-kickoff prologue sites on the private home world
 * (docs/design/soul-kickoff.md).
 *
 * The prologue is staged entirely on `home:<uid>` planet 0: the player makes
 * landfall at a cache ruin a walkable distance from the capital, crawls it,
 * and walks "home" to the living capital. Current characters instead begin
 * directly at a separately resolved clear founding site. Every anchor is a
 * pure function of the uid so client, server, and probes agree without any
 * data transfer (same contract as hometown selection).
 */
import { PlanetSampler, generateHomeSystem, homeCharacter, homeOwner } from "../universe.js";
import * as pmath from "../pmath.js";
import { hashSeed, mulberry32 } from "../rng.js";
import { tierDef } from "../homestead/types.js";
import { settlementInfluenceAt } from "../worldgen/settlements.js";
import type { CharacterHometownV1 } from "../creation.js";

/** Great-circle distance from the capital to the cache, metres. */
const CACHE_DISTANCE_M = 2400;
/** Legacy narrative "gates" anchor, inside the walk-home arrival ring. It is
 * no longer treated as a mechanically valid homestead site. */
const CAPITAL_GATE_M = 750;
/** Founding is mechanically separate from the narrative gate. Settlement
 * footprints can now exceed the old 750 m calibration, so scan outward until
 * the whole tier-0 claim plus the player's aimed-placement reach is clear. */
const FOUNDING_MIN_DISTANCE_M = 750;
const FOUNDING_MAX_DISTANCE_M = 3600;
const FOUNDING_DISTANCE_STEP_M = 150;
const FOUNDING_AIM_MARGIN_M = 14;
/** Extra candidate spacing when a bearing lands in water, metres. */
const CACHE_DISTANCE_STEP_M = 350;
/** Keep the cache dry: required clearance above the sea surface, metres. */
const SEA_CLEARANCE_M = 4;
const MAX_CANDIDATES = 24;
/** Golden-angle sweep so failed bearings never cluster. */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

export interface PrologueSitesV1 {
  systemId: string;
  planetIndex: 0;
  /** Planet-local anchor of the cache ruin (metres from planet centre). */
  cacheLocalPosition: [number, number, number];
  /** Unit tangent at the cache pointing toward the capital ("home"). */
  cacheLocalFacing: [number, number, number];
  /** Planet-local surface anchor of the capital centre — the walk-home target. */
  capitalLocalPosition: [number, number, number];
  /** Planet-local surface anchor at the legacy capital "gates" —
   * CAPITAL_GATE_M down the cache line from the centre. */
  gateLocalPosition: [number, number, number];
  /** Dry, deterministic build site outside every procedural-settlement
   * footprint, including enough margin to aim the first beacon nearby. */
  foundingLocalPosition: [number, number, number];
  /** Great-circle walk from cache to capital, metres. */
  distanceCacheToCapitalM: number;
}

function dot(a: readonly number[], b: readonly number[]): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function normalize(v: [number, number, number]): [number, number, number] {
  const n = pmath.hypot(v[0], v[1], v[2]);
  return n > 1e-9 ? [v[0] / n, v[1] / n, v[2] / n] : [1, 0, 0];
}

/** Rotate `dir` by `angle` radians along the tangent `bearing` (both unit, orthogonal). */
function slide(
  dir: readonly [number, number, number],
  bearing: readonly [number, number, number],
  angle: number,
): [number, number, number] {
  const c = pmath.cos(angle), s = pmath.sin(angle);
  return normalize([
    dir[0] * c + bearing[0] * s,
    dir[1] * c + bearing[1] * s,
    dir[2] * c + bearing[2] * s,
  ]);
}

/**
 * Resolve both prologue anchors for a character. Returns null when the
 * hometown record predates capital anchors or no dry cache site exists —
 * callers fall back to the legacy (stage-less) flow.
 */
export function resolvePrologueSites(
  uid: string,
  hometown: CharacterHometownV1,
): PrologueSitesV1 | null {
  if (homeOwner(hometown.systemId) !== uid) return null;
  const capital = hometown.capitalLocalPosition;
  if (!capital) return null;
  const system = generateHomeSystem(uid, homeCharacter(hometown.systemId) ?? undefined);
  const sampler = new PlanetSampler(system.planets[0]);
  const radius = sampler.def.radius;
  const seaRadius = sampler.def.seaRadius;
  const capitalDir = normalize([...capital]);

  // Stable tangent frame at the capital for candidate bearings.
  const ref: [number, number, number] = Math.abs(capitalDir[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const east = normalize([
    ref[1] * capitalDir[2] - ref[2] * capitalDir[1],
    ref[2] * capitalDir[0] - ref[0] * capitalDir[2],
    ref[0] * capitalDir[1] - ref[1] * capitalDir[0],
  ]);
  const north = normalize([
    capitalDir[1] * east[2] - capitalDir[2] * east[1],
    capitalDir[2] * east[0] - capitalDir[0] * east[2],
    capitalDir[0] * east[1] - capitalDir[1] * east[0],
  ]);

  const rng = mulberry32(hashSeed(`${sampler.def.seed}:prologue-cache`));
  const startBearing = rng() * Math.PI * 2;
  for (let i = 0; i < MAX_CANDIDATES; i++) {
    const theta = startBearing + i * GOLDEN_ANGLE;
    const bearing: [number, number, number] = normalize([
      east[0] * pmath.cos(theta) + north[0] * pmath.sin(theta),
      east[1] * pmath.cos(theta) + north[1] * pmath.sin(theta),
      east[2] * pmath.cos(theta) + north[2] * pmath.sin(theta),
    ]);
    const distM = CACHE_DISTANCE_M + (i >> 2) * CACHE_DISTANCE_STEP_M;
    const cacheDir = slide(capitalDir, bearing, distM / radius);
    const surface = sampler.surfaceRadius(cacheDir[0], cacheDir[1], cacheDir[2]);
    if (seaRadius !== null && surface < seaRadius + SEA_CLEARANCE_M) continue;

    // Face the walk home: tangent at the cache pointing toward the capital.
    const toward: [number, number, number] = [
      capitalDir[0] - cacheDir[0] * dot(capitalDir, cacheDir),
      capitalDir[1] - cacheDir[1] * dot(capitalDir, cacheDir),
      capitalDir[2] - cacheDir[2] * dot(capitalDir, cacheDir),
    ];
    const capitalSurface = sampler.surfaceRadius(capitalDir[0], capitalDir[1], capitalDir[2]);
    // The gates: slide from the capital centre toward the cache along the
    // great circle, then seat on the local surface (terrain can differ by
    // tens of metres over 750 m — a tangent offset would spawn underground).
    const gateBearing = normalize([
      cacheDir[0] - capitalDir[0] * dot(cacheDir, capitalDir),
      cacheDir[1] - capitalDir[1] * dot(cacheDir, capitalDir),
      cacheDir[2] - capitalDir[2] * dot(cacheDir, capitalDir),
    ]);
    const gateDir = slide(capitalDir, gateBearing, CAPITAL_GATE_M / radius);
    const gateSurface = sampler.surfaceRadius(gateDir[0], gateDir[1], gateDir[2]);
    let foundingLocalPosition: [number, number, number] | null = null;
    for (let foundingDist = FOUNDING_MIN_DISTANCE_M;
      foundingDist <= FOUNDING_MAX_DISTANCE_M; foundingDist += FOUNDING_DISTANCE_STEP_M) {
      const foundingDir = slide(capitalDir, gateBearing, foundingDist / radius);
      const foundingSurface = sampler.surfaceRadius(foundingDir[0], foundingDir[1], foundingDir[2]);
      if (seaRadius !== null && foundingSurface < seaRadius + SEA_CLEARANCE_M) continue;
      const clearance = tierDef(0).radius + FOUNDING_AIM_MARGIN_M;
      if (settlementInfluenceAt(
        sampler, foundingDir[0], foundingDir[1], foundingDir[2], clearance,
      ) !== null) continue;
      foundingLocalPosition = [
        foundingDir[0] * (foundingSurface + 1.05),
        foundingDir[1] * (foundingSurface + 1.05),
        foundingDir[2] * (foundingSurface + 1.05),
      ];
      break;
    }
    if (!foundingLocalPosition) continue;
    return {
      systemId: hometown.systemId,
      planetIndex: 0,
      cacheLocalPosition: [
        cacheDir[0] * (surface + 1.05),
        cacheDir[1] * (surface + 1.05),
        cacheDir[2] * (surface + 1.05),
      ],
      cacheLocalFacing: normalize(toward),
      capitalLocalPosition: [
        capitalDir[0] * (capitalSurface + 1.05),
        capitalDir[1] * (capitalSurface + 1.05),
        capitalDir[2] * (capitalSurface + 1.05),
      ],
      gateLocalPosition: [
        gateDir[0] * (gateSurface + 1.05),
        gateDir[1] * (gateSurface + 1.05),
        gateDir[2] * (gateSurface + 1.05),
      ],
      foundingLocalPosition,
      distanceCacheToCapitalM: Math.round(distM),
    };
  }
  return null;
}
